import { NUMBER_LOCALE } from './number-format';

export { caretForDigitIndex, digitsBeforeCaret } from './number-format';

/**
 * Single shared place for the app's monetary locale + grouping convention.
 *
 * The app targets the European market: every monetary value is EUR and every
 * amount is grouped in the `de-DE` convention (`.` as the thousands separator).
 * The masked {@link CurrencyInput} and the read-only `currencyFormatter`
 * displays must stay consistent, so the locale and symbol live here rather than
 * being hard-coded per call site. The locale and the caret helpers come from
 * the shared `number-format` module so the currency and quantity masks never
 * fork divergent grouping or caret logic.
 */
export const CURRENCY_LOCALE = NUMBER_LOCALE;
export const CURRENCY_SYMBOL = '€';

const groupingFormatter = new Intl.NumberFormat(CURRENCY_LOCALE, {
  useGrouping: true,
  maximumFractionDigits: 0,
});

/**
 * Read-only display formatter for whole-Euro amounts („450.000 €"). Lives here
 * so display surfaces share one spec with the input masks above instead of
 * re-declaring the locale/currency per page (deals/format.ts, properties.tsx
 * and leads/detail.tsx predate this and can migrate).
 */
export const euroFormatter = new Intl.NumberFormat(CURRENCY_LOCALE, {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/**
 * Strips every non-digit character from a raw input string, returning only the
 * digits the user typed. Whole-Euro fields carry no fractional part, so a
 * decimal separator is intentionally not preserved here.
 */
export function digitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

/**
 * Formats a digit string with `de-DE` thousands separators. An empty string
 * stays empty (so a cleared field never renders `0`); leading zeros are
 * normalised away by the numeric round-trip.
 */
export function formatDigits(digits: string): string {
  if (digits === '') return '';

  const value = Number(digits);

  if (!Number.isFinite(value)) return '';

  return groupingFormatter.format(value);
}

/**
 * Formats a stored numeric amount for display on mount. `null` renders empty,
 * distinguishing "no value" from `0`.
 */
export function formatAmount(amount: number | null): string {
  if (amount === null) return '';

  return groupingFormatter.format(amount);
}

const centsFormatter = new Intl.NumberFormat(CURRENCY_LOCALE, {
  useGrouping: true,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a stored amount for a {@link CurrencyInput} display. Whole amounts
 * never grow a „,00"; with `decimals` 2 a fractional amount shows both cent
 * digits, otherwise it rounds to whole Euros.
 */
export function formatStoredAmount(amount: number | null, decimals: 0 | 2): string {
  if (amount === null) return '';

  if (Number.isInteger(amount) || decimals === 0) return formatAmount(amount);

  return centsFormatter.format(amount);
}

/**
 * Parses a formatted/raw input string back to its clean numeric value, or
 * `null` when no digits were entered. Never coerces an empty field to `0`.
 */
export function parseAmount(raw: string): number | null {
  const digits = digitsOnly(raw);

  if (digits === '') return null;

  return Number(digits);
}
